import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { useGetCatalogQuery, useGetReleasesQuery } from '../../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { Panel } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { PromoteContainer } from '../../PromoteContainer';
import { smlNavigationMessages } from '../../../navigation/smlNavigationMessages';
import { releaseListMessages } from './releaseListMessages';
import { groupReleaseEntries } from './releaseListEntries';
import { ReleasesListBody } from './ReleasesListBody';
import { ReleasesEmpty } from './ReleasesEmpty';
import { AllFilesButton } from './allFiles/AllFilesButton';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasesList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleasesListProps {
  accountId: number;
}

/**
 * Promote / Releases (Releases v2, design a1–a3): every catalog song is a release. Three groups:
 * Upcoming (soonest first), Active (something made, posted or planned in the last 30 days) and
 * Quiet (closed, with a count). Each row leads with one next step; the counts sit on the right.
 * The server orders the rows and picks the next step (`GET /releases`); the catalog gives the
 * title, the cover and the date.
 */
const ReleasesListComponent: React.FC<ReleasesListProps> = ({ accountId }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const catalog = useGetCatalogQuery({ accountId });
  const releases = useGetReleasesQuery({ accountId });
  const [query, setQuery] = useState('');

  // One "today" for the whole screen, so every "Out in N days" agrees.
  const now = useMemo(() => new Date(), []);
  const groups = useMemo(
    () => groupReleaseEntries(catalog.data?.items ?? [], releases.data?.releases ?? [], query),
    [catalog.data, releases.data, query]
  );

  const openStudio = useCallback(() => router.push(SMLRoutes.promoteStudio), [router]);
  const onQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), []);
  const songTitles = useMemo(() => (catalog.data?.items ?? []).map((item) => item.title), [catalog.data]);
  const { refetch: refetchCatalog } = catalog;
  const { refetch: refetchReleases } = releases;
  const retry = useCallback(() => {
    refetchCatalog().catch(console.error);
    refetchReleases().catch(console.error);
  }, [refetchCatalog, refetchReleases]);

  const error = catalog.error ?? releases.error;
  let body: React.ReactNode;
  if (catalog.isLoading || releases.isLoading) {
    body = <LoadingView />;
  } else if (error !== undefined) {
    body = <ErrorView error={error} retry={retry} />;
  } else if ((catalog.data?.items.length ?? 0) === 0) {
    body = <ReleasesEmpty />;
  } else {
    body = (
      <Panel>
        <ReleasesListBody groups={groups} now={now} query={query} />
      </Panel>
    );
  }

  const actions = (
    <>
      <div className={styles.findBox}>
        <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
        <input
          className={styles.findInput}
          type="search"
          value={query}
          onChange={onQuery}
          placeholder={intl.formatMessage(releaseListMessages.find)}
          aria-label={intl.formatMessage(releaseListMessages.find)}
        />
      </div>
      {/* design: assets-rethink d4. No Library page: "All files" finds any file of the artist. */}
      <AllFilesButton accountId={accountId} songTitles={songTitles} />
      <SMLButton onClick={openStudio} icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}>
        {intl.formatMessage(releaseListMessages.openStudio)}
      </SMLButton>
    </>
  );

  return (
    <PromoteContainer
      title={intl.formatMessage(smlNavigationMessages.promoteReleases)}
      subtitle={intl.formatMessage(releaseListMessages.subtitle)}
      actions={actions}
    >
      {body}
    </PromoteContainer>
  );
};

export const ReleasesList = TypedMemo(ReleasesListComponent);
